import { findByVendor } from "../../utils/itemPricingFunctions";
import { COLES, WOOLWORTHS } from "../enums";
import { ACTION_TYPES, ActionType } from "../../types/shopify";
import { sortItems as applySortToItems } from "../../utils/sortUtils";

// Helper function to convert user-friendly sort names to API sort types
function getApiSortTypeFromName(sortTypeName: string): string {
  if (sortTypeName === "Name (A-Z)") return "A-Z";
  if (sortTypeName === "Name (Z-A)") return "Z-A";
  if (sortTypeName === "Price (High to Low)") return "HighestPrice";
  if (sortTypeName === "Price (Low to High)") return "LowestPrice";
  if (sortTypeName === "Biggest Savings") return "Difference";
  return "A-Z"; // Default
}

const initialState: ItemsState = {
  itemsToDisplay: [],
  matchedItem: "",
  closeMatchId: 0,
  unfilteredItems: [],
  // Store all unfiltered items for restoration when filters are removed
  allItems: [],
  // Store the original items before half-price filtering
  originalItemsBeforeHalfPrice: [],
  sortTypeName: "",
  appliedFilters: [],
  currentPageNumber: 0,
  currentItemId: 1,
  totalPages: 0,
  totalProductsFound: 0,
  isUnavailableProductsHidden: true,
  isHalfThePriceClicked: false,
  // Track how many consecutive empty batches we've received when filtering for half-price items
  emptyHalfPriceBatchCount: 0,
  paginationInfo: {
    hasNextPage: false,
    endCursor: null,
    collectionHandle: null,
    searchTerm: null
  },
  isLoading: false,
  isLoadingMore: false,
  error: null
};

// Add a flag to track if we have active search results
let hasActiveSearchResults = false;

// Function to check if this is a category-specific product load
function isCategoryNavigation(action: ItemAction): boolean {
  // Check if there's a specific collection handle in the pagination info
  if (action.paginationInfo?.collectionHandle) {
    return true;
  }
  
  // Check if the URL pattern indicates category navigation
  try {
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length > 2 && pathParts[1] === 'browse') {
      return true; // URL pattern indicates category navigation
    }
  } catch (e) {
    // Ignore errors from accessing window.location in SSR or tests
  }
  
  return false;
}

// Expose function to reset search flag (can be called from outside)
export function resetActiveSearchFlag() {
  console.log('EXPLICITLY resetting active search flag');
  hasActiveSearchResults = false;
}

export default function filterReducer(
  state = initialState,
  action: ItemAction
) {
  // Cast the action type to our union type
  const actionType = action.type as ActionType;
  
  switch (actionType) {
    case ACTION_TYPES.DISPLAY_ITEMS:
      console.log('REDUCER: DISPLAY_ITEMS action received with', action.itemsToDisplay?.length || 0, 'items');
      console.log('REDUCER: First item in action:', action.itemsToDisplay?.length > 0 ? action.itemsToDisplay[0] : 'No items');
      
      // Determine if this is a search result, homepage products, or category navigation
      const isSearchResult = action.paginationInfo?.searchTerm && action.paginationInfo.searchTerm.trim() !== '';
      const isHomepageProducts = !isSearchResult && action.itemsToDisplay?.length > 50; // Homepage typically has many products
      const isCategoryNav = isCategoryNavigation(action);
      
      console.log(`REDUCER: Handling ${isSearchResult ? 'SEARCH results' : isCategoryNav ? 'CATEGORY products' : isHomepageProducts ? 'HOMEPAGE products' : 'OTHER products'} dispatch`);
      
      // CRITICAL FIX: If this is a category selection, FORCE clear the search flag
      // This ensures that category navigation works properly after search
      if (isCategoryNav) {
        console.log('REDUCER: Category navigation detected, FORCING reset of hasActiveSearchResults flag');
        hasActiveSearchResults = false;
      }
      
      // Determine if we should keep the search results instead of showing homepage products
      if (isHomepageProducts && hasActiveSearchResults && !isCategoryNav) {
        console.log('REDUCER: Preventing homepage products from overriding active search results');
        return state; // Keep current state with search results
      }
      
      // If this is search results, mark that we have active search results
      if (isSearchResult) {
        console.log(`REDUCER: Setting hasActiveSearchResults=true for "${action.paginationInfo.searchTerm}"`);
        hasActiveSearchResults = true;
      } else if (!isSearchResult && action.paginationInfo?.searchTerm === null) {
        // Reset the flag if we're explicitly not searching (searchTerm is null, not empty string)
        console.log('REDUCER: Clearing hasActiveSearchResults flag');
        hasActiveSearchResults = false;
      }
      
      const unfilteredItems = initialItems(action.itemsToDisplay);
      console.log('REDUCER: After initialItems processing:', unfilteredItems?.length || 0, 'items');

      return {
        ...state,
        sortTypeName: action.sortTypeName,
        itemsToDisplay: displayItems(unfilteredItems, state),
        matchedItem: action.matchedItem,
        closeMatchId: action.closeMatchId,
        unfilteredItems: unfilteredItems,
        // Store a copy of all items for restoring when filters are turned off
        allItems: unfilteredItems,
        currentPageNumber: action.pageNumber,
        currentItemId: action.currentItemId,
        totalPages: action.totalPages,
        totalProductsFound: action.totalProductsFound,
        paginationInfo: action.paginationInfo || initialState.paginationInfo,
        error: null
      };

    case ACTION_TYPES.GET_MORE_ITEMS:
      // if there are extra items
      if (action?.itemsToDisplay?.length) {
        console.log(`GET_MORE_ITEMS with ${action.itemsToDisplay.length} new items`);
        
        // Process the new items
        let updatedItems = initialItems(action.itemsToDisplay);
        
        // add the additional products to our unfiltered items collection
        let allSearchedItems = state?.unfilteredItems.concat(updatedItems);
        
        // Get current sort type information - prioritize action params over state
        const sortTypeName = action.sortTypeName || state.sortTypeName || '';
        const apiSortType = action.sortType || (sortTypeName ? getApiSortTypeFromName(sortTypeName) : '');
        
        console.log(`GET_MORE_ITEMS sort info: Name=${sortTypeName}, Type=${apiSortType}`);
        
        // First apply filters to get the right set of items
        let displayedItems = displayItems(allSearchedItems, state);
        
        // CRITICAL: ALWAYS re-sort the ENTIRE collection (both old and new items)
        // This ensures items are properly sorted according to our custom logic
        if (apiSortType) {
          console.log(`Re-sorting all ${displayedItems.length} items with ${apiSortType}`);
          // Force a fresh sort of the combined items
          displayedItems = applySortToItems(displayedItems, apiSortType);
        }

        return {
          ...state,
          sortTypeName: sortTypeName,
          itemsToDisplay: displayedItems,
          unfilteredItems: allSearchedItems,
          // Update allItems with the merged items when getting more items
          allItems: allSearchedItems,
          currentPageNumber: action.pageNumber,
          currentItemId: action.currentItemId,
          totalPages: action.totalPages,
          totalProductsFound: action.totalProductsFound || state.totalProductsFound,
          paginationInfo: action.paginationInfo || state.paginationInfo,
          error: null
        };
      } else {
        // do nothing
        return { ...state };
      }

    case ACTION_TYPES.SET_LOADING_STATE:
      return {
        ...state,
        isLoading: action.isLoading
      };

    case ACTION_TYPES.SET_LOADING_MORE_STATE:
      return {
        ...state,
        isLoadingMore: action.isLoadingMore
      };

    case ACTION_TYPES.SET_ERROR_STATE:
      return {
        ...state,
        error: action.error || null,
        isLoading: false,
        isLoadingMore: false
      };

    case ACTION_TYPES.SET_EMPTY_HALF_PRICE_BATCH_COUNT: {
      console.log(`Setting empty half-price batch count to ${action.count}`);
      return {
        ...state,
        emptyHalfPriceBatchCount: action.count
      };
    }
    
    // Update pagination info - especially important for proper cursor handling
    case ACTION_TYPES.SET_PAGINATION_INFO: {
      console.log(`Updating pagination info: hasNextPage=${action.paginationInfo.hasNextPage}, cursor=${action.paginationInfo.endCursor || 'null'}`);
      return {
        ...state,
        paginationInfo: action.paginationInfo
      };
    }

    case ACTION_TYPES.SORT_ITEMS:
      const itemsToSort = state?.itemsToDisplay || [];
      let sortType = action.sortType ? action.sortType : state?.sortTypeName;
      let sortTypeName = '';
      
      // Convert sortType to sortTypeName for display
      if (sortType === 'A-Z') sortTypeName = 'Name (A-Z)';
      else if (sortType === 'Z-A') sortTypeName = 'Name (Z-A)';
      else if (sortType === 'HighestPrice') sortTypeName = 'Price (High to Low)';
      else if (sortType === 'LowestPrice') sortTypeName = 'Price (Low to High)';
      else if (sortType === 'Difference') sortTypeName = 'Biggest Savings';
      
      console.log(`Reducer: Sorting ${itemsToSort.length} items with sort type: ${sortType}`);
      
      // Use the utility function to sort the items
      const sortedItems = applySortToItems(itemsToSort, sortType);
      
      return {
        ...state,
        itemsToDisplay: sortedItems,
        sortTypeName: sortTypeName,
      };

    case ACTION_TYPES.REMOVE_FILTER:
      const currentlyAppliedFilters = [...state.appliedFilters];

      // remove the filter
      const index = currentlyAppliedFilters.findIndex(
        (filter) => filter.filter_id === action.filter?.filter_id
      );
      currentlyAppliedFilters.splice(index, 1);

      return {
        ...state,
        itemsToDisplay: displayItems(state.unfilteredItems, {
          ...state,
          appliedFilters: currentlyAppliedFilters,
        }),
        appliedFilters: currentlyAppliedFilters,
      };

    case ACTION_TYPES.FILTER_ITEMS:
      let filtered: ItemData[] = [];

      // apply the additional filter
      if (action.filter?.enum === "Special") {
        filtered = state.itemsToDisplay.filter((item) => item.isSpecial);
      } else if (action.filter?.enum === "MatchingItemsOnly") {
        filtered = state.itemsToDisplay.filter(
          (item) => item.matchingItemsOnly
        );
      } else if (action.filter?.enum === "ExclusiveColes") {
        filtered = state.itemsToDisplay.filter((item) => item.exclusiveColes);
      } else if (action.filter?.enum === "ExclusiveWools") {
        filtered = state.itemsToDisplay.filter((item) => item.exclusiveWools);
      } else if (action.filter?.enum === "AvailableAtBoth") {
        filtered = state.itemsToDisplay.filter((item) => item.availableAtBoth);
      }

      return {
        ...state,
        itemsToDisplay: displayItems(filtered, state),
        appliedFilters: [...state.appliedFilters, action.filter], // add additional filter_id to array
      };

    case ACTION_TYPES.TOGGLE_UNAVAILABLE_PRODUCTS:
      let itemsToDisplay = toggleUnavailableProducts(
        state,
        action.isUnavailableProductsHidden
      );

      state.isUnavailableProductsHidden = action.isUnavailableProductsHidden;
      itemsToDisplay = displayItems(itemsToDisplay, state);

      return {
        ...state,
        itemsToDisplay,
      };

    case ACTION_TYPES.SAVE_ORIGINAL_ITEMS:
      // This action is specifically for saving the original, unfiltered items
      // before applying half-price filtering so we can restore them later
      console.log(`Saving ${action.originalItems?.length || 0} original items for later restoration`);
      return {
        ...state,
        originalItemsBeforeHalfPrice: action.originalItems || [],
      };

    case ACTION_TYPES.TOGGLE_HALF_THE_PRICE_PRODUCTS:
      // This action now only sets the isHalfThePriceClicked flag
      // The actual filtering is done in the itemActions.ts file
      console.log(`Setting half-price flag to: ${action.isHalfThePriceClicked}`);
      return {
        ...state,
        isHalfThePriceClicked: action.isHalfThePriceClicked,
      };

    case ACTION_TYPES.GET_MATCHED_ITEMS:
      const itemsToCheck = state?.itemsToDisplay;
      let matchedItems;

      itemsToCheck.forEach((item) => {
        if (
          item.name.toLowerCase().includes("coles") ||
          item.name.toLowerCase().includes("woolworths")
        ) {
          matchedItems = item;
        }
      });

      return {
        ...state,
        matchedItems: matchedItems,
      };
    default:
      return state;
  }
}

function applyFiltersToItems(
  items: ItemData[],
  currentlyAppliedFilters: FilterOption[]
) {
  let newFilteredItems = [...items];

  // if filtering on specials
  if (
    currentlyAppliedFilters.findIndex((filter) => filter.enum === "Special") >
    -1
  ) {
    newFilteredItems = items.filter((item) => item.isSpecial);
  }
  // if filtering on matching items only
  if (
    currentlyAppliedFilters.findIndex(
      (filter) => filter.enum === "MatchingItemsOnly"
    ) > -1
  ) {
    newFilteredItems = items.filter((item) => item.matchingItemsOnly);
  }
  // if filtering on coles
  if (
    currentlyAppliedFilters.findIndex(
      (filter) => filter.enum === "ExclusiveColes"
    ) > -1
  ) {
    newFilteredItems = items.filter((item) => item.exclusiveColes);
  }
  // if filtering on wools
  if (
    currentlyAppliedFilters.findIndex(
      (filter) => filter.enum === "ExclusiveWools"
    ) > -1
  ) {
    newFilteredItems = items.filter((item) => item.exclusiveWools);
  }
  // if filtering on Available at both
  if (
    currentlyAppliedFilters.findIndex(
      (filter) => filter.enum === "AvailableAtBoth"
    ) > -1
  ) {
    newFilteredItems = items.filter((item) => item.availableAtBoth);
  }

  return newFilteredItems;
}

function isItemSpecial(item: ItemData) {
  // Use name instead of title for logging since title might not exist on all items
  console.log(`[DEBUG] isItemSpecial called for ${item.name}, isSpecial=${item.isSpecial}`);
  
  // If the item already has isSpecial set from Shopify metafields, respect that value
  if (item.isSpecial !== undefined) {
    return item.isSpecial;
  }
  
  // Otherwise, fall back to the original calculation logic
  const { item_pricing } = item;

  if (item_pricing.length === 0) {
    return false;
  }

  return item_pricing.some((pricing) => pricing.special);
}

function isItemHalfThePrice(item: ItemData) {
  // Use name instead of title for logging since title might not exist on all items
  console.log(`[DEBUG] isItemHalfThePrice called for ${item.name}, isHalfThePrice=${item.isHalfThePrice}`);
  
  // If the item already has isHalfThePrice set from Shopify metafields, respect that value
  if (item.isHalfThePrice !== undefined) {
    return item.isHalfThePrice;
  }
  
  // Otherwise, fall back to the original calculation logic
  if (!isItemSpecial(item)) {
    return false;
  }

  const { item_pricing } = item;

  const special = item_pricing.find((pricing) => pricing.special);

  if (!special) {
    return false;
  }

  // Make sure was_price exists before using it
  return special.price <= (special.was_price || 0) / 2;
}

function displayItems(items: ItemData[], options: ItemsState) {
  console.log('displayItems called with', items?.length || 0, 'items');
  if (!items || items.length === 0) {
    console.log('WARNING: No items to display in displayItems');
    return [];
  }
  
  const currentlyAppliedItemFilters = options.appliedFilters;

  if (currentlyAppliedItemFilters && currentlyAppliedItemFilters.length > 0) {
    items = applyFiltersToItems(items, currentlyAppliedItemFilters);
  }

  if (options.isUnavailableProductsHidden) {
    items = items.filter((item) =>
      item.item_pricing.some((pricing) => pricing.available)
    );
  }

  // Note: Half-price filtering is now handled directly in the TOGGLE_HALF_THE_PRICE_PRODUCTS case
  // This is kept for backward compatibility but may not be needed
  if (options.isHalfThePriceClicked) {
    // Filter based on the isHalfThePrice property which comes from Shopify metafields
    items = items.filter((item) => item.isHalfThePrice === true);
  }

  return items || [];
}

function initialItems(itemsToDisplay?: ItemData[]) {
  console.log('initialItems called with', itemsToDisplay?.length || 0, 'items');
  if (!itemsToDisplay || itemsToDisplay.length === 0) {
    console.log('WARNING: No items to display in initialItems');
    return [];
  }
  
  itemsToDisplay?.forEach((item) => {
    // Don't override isSpecial and isHalfThePrice if they're already set from Shopify metafields
    // Only calculate them if they're not already set
    if (item.isSpecial === undefined) {
      item.isSpecial = isItemSpecial(item);
    }
    
    if (item.isHalfThePrice === undefined) {
      item.isHalfThePrice = isItemHalfThePrice(item);
    }

    const { close_match_a } = item;

    if (close_match_a !== null && close_match_a?.item_a) {
      if (close_match_a.item_a.isSpecial === undefined) {
        close_match_a.item_a.isSpecial = isItemSpecial(close_match_a.item_a);
      }
    }
    // item is exclusive if wools do not sell it
    item.exclusiveColes = !item.item_pricing.some(
      (pricing) => pricing.vendor_id === 2
    );
    // item is exclusive if coles do not sell it
    item.exclusiveWools = !item.item_pricing.some(
      (pricing) => pricing.vendor_id === 1
    );

    // item is available at both
    item.availableAtBoth = isItemAvailableAtBothVendors(item.item_pricing);
  });

  return itemsToDisplay || [];
}

function isItemAvailableAtBothVendors(itemPricings: ItemPricings[]) {
  if (itemPricings && itemPricings.length === 2) {
    const itemOnColes = findByVendor(itemPricings, COLES);
    const itemOnWoolWorths = findByVendor(itemPricings, WOOLWORTHS);

    return (
      itemOnColes?.available &&
      itemOnWoolWorths?.available &&
      itemOnColes?.price !== 0 &&
      itemOnWoolWorths?.price !== 0
    );
  }
  return false;
}

function toggleUnavailableProducts(
  state: ItemsState,
  isUnavailableProductsHidden: boolean | undefined
) {
  if (isUnavailableProductsHidden) {
    return state.itemsToDisplay.filter((item) =>
      item.item_pricing.some((pricing) => pricing.available)
    );
  }

  return [...state.unfilteredItems];
}

// Function removed - half-price filtering is now handled in the itemActions.ts file

// Handle setting empty half-price batch count
export function handleSetEmptyHalfPriceBatchCount(state: ItemsState, count: number): ItemsState {
  return {
    ...state,
    emptyHalfPriceBatchCount: count
  };
}
